import { useCallback, useEffect, useRef } from 'react';
import { useDuckDB } from '../context/LazyDuckDBContext';
import { useFilters } from '../context/FiltersContext';

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Hook to prefetch critical dashboard data while the user is still on the landing page.
 * This populates the DuckDB query cache so transitions feel "instant".
 */
export const usePrefetchDashboard = () => {
  const { query, ready } = useDuckDB();
  const { filters } = useFilters();
  const prefetchedRef = useRef<Set<string>>(new Set());

  const prefetch = useCallback(async (kommunId: string, year: number) => {
    if (!ready || !kommunId) return;

    const cacheKey = `${kommunId}-${year}`;
    if (prefetchedRef.current.has(cacheKey)) return;
    
    prefetchedRef.current.add(cacheKey);
    if (isDevelopment) console.log(`🚀 Prefetching dashboard data for ${kommunId} (${year})...`);

    try {
      // 1. Get latest year (critical first step in useKommunData)
      const latestYearResult = await query<{ latest_year: number }[]>(`
        SELECT MAX(year) AS latest_year
        FROM gold_kpi_muni_year
        WHERE municipality_id = :kommunId AND year <= :year
      `, { kommunId, year });

      const latestYear = latestYearResult[0]?.latest_year ?? year;

      // 2. Prefetch the "Aggregated KPI" query used for the summary hero and groups
      // This is the largest query in useKommunData
      const kpiIds = [
        'N15504', 'N15501', 'N15418', 'N15436', 'N15494', 'N15502', 'N15548', 'N15497',
        'N15482', 'N15488', 'N15516', 'N15485',
        'N15033', 'N15809', 'N15813',
        'U15416'
      ];
      const kpiList = kpiIds.map(id => `'${id}'`).join(', ');
      
      const startYear = year - 6;
      const endYear = year;

      // 3. Prefetch the distribution for the default KPI (N15504 - Meritvärde)
      // This is often shown immediately in the "Snabböversikt"
      const distributionQuery = `
          WITH KommunMedel AS (
            SELECT AVG(value_t) AS mu
            FROM gold_kpi_muni_year
            WHERE municipality_id = :kommunId
              AND kpi_id = 'N15504'
              AND year = :latestYear
              AND value_t IS NOT NULL
          ),
          SchoolValues AS (
            SELECT t.entity_id AS ou_id, t.entity_name AS ou_name, AVG(t.value) AS value
            FROM gold_kpi_trend t
            WHERE t.municipality_id = :kommunId AND t.kpi_id = 'N15504' AND t.gender = 'T'
              AND t.entity_type = 'org_unit' AND t.year = :latestYear
              AND t.value IS NOT NULL
            GROUP BY t.entity_id, t.entity_name
          ),
          Params AS (SELECT mu, 3.0 AS tol FROM KommunMedel)
          SELECT s.ou_id, s.ou_name, s.value, s.value - p.mu AS diff_mot_kommunmedel, p.mu AS kommunMedel,
            CASE WHEN s.value >= p.mu + p.tol THEN 'over' WHEN s.value <= p.mu - p.tol THEN 'under' ELSE 'inline' END AS color_class
          FROM SchoolValues s CROSS JOIN Params p ORDER BY s.value DESC
        `;

      // Run these in parallel
      await Promise.all([
        query(`
          WITH base AS (
            SELECT
              kpi_id,
              year,
              CASE
                WHEN SUM(CASE WHEN students > 0 THEN students ELSE 0 END) > 0
                  THEN SUM(value * students) / NULLIF(SUM(students), 0)
                ELSE AVG(value)
              END AS value
            FROM gold_kpi_trend
            WHERE municipality_id = :kommunId AND kpi_id IN (${kpiList})
              AND entity_type = 'org_unit' AND gender = 'T'
              AND year BETWEEN :startYear AND :endYear
              AND value IS NOT NULL
            GROUP BY kpi_id, year
          )
          SELECT kpi_id, year, value, LAG(value) OVER (PARTITION BY kpi_id ORDER BY year) AS prev_year_value
          FROM base ORDER BY kpi_id, year
        `, { kommunId, startYear, endYear }),

        query(`
          SELECT kpi_id, year, AVG(value_t) AS value
          FROM gold_kpi_muni_year
          WHERE kpi_id IN (${kpiList})
            AND year BETWEEN :startYear AND :endYear
            AND value_t IS NOT NULL
          GROUP BY kpi_id, year
        `, { kommunId, startYear, endYear }),

        query(distributionQuery, { kommunId, latestYear })
      ]);

      if (isDevelopment) console.log(`✅ Prefetch complete for ${kommunId}`);
    } catch (err) {
      console.warn('Prefetch failed:', err);
      // Don't mark as prefetched if it failed
      prefetchedRef.current.delete(cacheKey);
    }
  }, [query, ready]);

  // Automatically prefetch if we have a kommunId in filters
  useEffect(() => {
    if (ready && filters.kommunId) {
      // Use a small delay to not block the main thread if there's other stuff happening
      const timer = setTimeout(() => {
        prefetch(filters.kommunId!, filters.year || 2024);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [ready, filters.kommunId, filters.year, prefetch]);

  return { prefetch };
};

